/**
 * Pagination Component
 *
 * A reusable pagination component with page navigation and items per page selector.
 *
 * @since 1.0.0
 */

import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useMemo } from "react";

import {
  Ellipsis,
  ItemsPerPageContainer,
  ItemsPerPageSelect,
  NavigationContainer,
  PageButton,
  PageInfo,
  PaginationContainer,
} from "./styled";
import type { PaginationProps } from "./types";

/**
 * Pagination component
 *
 * @since 1.0.0
 *
 * @param props Component props.
 *
 * @return JSX element.
 */
export const Pagination = (props: PaginationProps): JSX.Element => {
  const {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,
    showItemsPerPageSelector = true,
    itemsPerPageOptions = [10, 25, 50, 100],
    maxPageButtons = 7,
    disabled = false,
    className = "",
    showPageInfo = true,
    showFirstLastButtons = true,
    strings,
  } = props;

  /**
   * Calculate page numbers to display
   *
   * @since 1.0.0
   *
   * @return Array of page numbers and ellipsis indicators.
   */
  const pageNumbers = useMemo(() => {
    const pages: (number | "ellipsis")[] = [];

    if (totalPages <= maxPageButtons) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate range around current page
      const sidePages = Math.floor((maxPageButtons - 3) / 2); // Reserve space for first, last, and ellipsis

      const startPage = Math.max(2, currentPage - sidePages);
      const endPage = Math.min(totalPages - 1, currentPage + sidePages);

      // Always show first page
      pages.push(1);

      // Add ellipsis if needed
      if (startPage > 2) {
        pages.push("ellipsis");
      }

      // Add pages around current page
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pages.push("ellipsis");
      }

      // Always show last page (if not already included)
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages, maxPageButtons]);

  /**
   * Calculate the current range of items being shown
   *
   * @since 1.0.0
   */
  const itemRange = useMemo(() => {
    if (totalItems === 0) return { startItem: 0, endItem: 0 };
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);
    return { startItem, endItem };
  }, [currentPage, itemsPerPage, totalItems]);

  /**
   * Handle page change
   *
   * @since 1.0.0
   *
   * @param page Page number to navigate to.
   */
  const handlePageChange = (page: number): void => {
    if (disabled || page < 1 || page > totalPages || page === currentPage) {
      return;
    }
    onPageChange(page);
  };

  /**
   * Handle items per page change
   *
   * @since 1.0.0
   *
   * @param event Select change event.
   */
  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    onItemsPerPageChange?.(newItemsPerPage);
  };

  // Don't render if there's only one page and no items per page selector
  if (totalPages <= 1 && !showItemsPerPageSelector) {
    return <></>;
  }

  return (
    <PaginationContainer className={`redfox-pagination ${className}`.trim()}>
      {/* Page Info */}
      {showPageInfo && (
        <PageInfo>
          {(() => {
            if (totalItems === 0) return strings.noItems;
            if (totalItems === 1) return strings.oneItem;
            return `${strings.showing} ${itemRange.startItem}-${itemRange.endItem} ${strings.of} ${totalItems} ${strings.items}`;
          })()}
        </PageInfo>
      )}

      {/* Page Navigation */}
      {totalPages > 1 && (
        <NavigationContainer>
          {/* First Page Button */}
          {showFirstLastButtons && (
            <PageButton
              onClick={() => handlePageChange(1)}
              disabled={disabled || currentPage === 1}
              title={strings.firstPage}
              aria-label={strings.goToFirstPage}
            >
              <ChevronDoubleLeftIcon width={16} height={16} />
            </PageButton>
          )}

          {/* Previous Page Button */}
          <PageButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={disabled || currentPage === 1}
            title={strings.previousPage}
            aria-label={strings.goToPreviousPage}
          >
            <ChevronLeftIcon width={16} height={16} />
          </PageButton>

          {/* Page Numbers */}
          {pageNumbers.map((page, index) => {
            if (page === "ellipsis") {
              return <Ellipsis key={`ellipsis-${index}`}>…</Ellipsis>;
            }

            return (
              <PageButton
                key={page}
                onClick={() => handlePageChange(page)}
                disabled={disabled}
                $isActive={page === currentPage}
                title={`${strings.page} ${page}`}
                aria-label={`${strings.goToPage} ${page}`}
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </PageButton>
            );
          })}

          {/* Next Page Button */}
          <PageButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={disabled || currentPage === totalPages}
            title={strings.nextPage}
            aria-label={strings.goToNextPage}
          >
            <ChevronRightIcon width={16} height={16} />
          </PageButton>

          {/* Last Page Button */}
          {showFirstLastButtons && (
            <PageButton
              onClick={() => handlePageChange(totalPages)}
              disabled={disabled || currentPage === totalPages}
              title={strings.lastPage}
              aria-label={strings.goToLastPage}
            >
              <ChevronDoubleRightIcon width={16} height={16} />
            </PageButton>
          )}
        </NavigationContainer>
      )}

      {/* Items Per Page Selector */}
      {showItemsPerPageSelector && onItemsPerPageChange && (
        <ItemsPerPageContainer>
          <label htmlFor="items-per-page">{strings.itemsPerPage}</label>
          <ItemsPerPageSelect
            id="items-per-page"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            disabled={disabled}
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </ItemsPerPageSelect>
        </ItemsPerPageContainer>
      )}
    </PaginationContainer>
  );
};
